"use client";

import { useEffect, useRef } from "react";
import keycloak from "@/lib/keycloak";
import { useAuthStore } from "@/store/authStore";
import type { UserRole } from "@/types";

function extractRole(tokenParsed: Record<string, any> | undefined): UserRole {
  const roles: string[] = tokenParsed?.realm_access?.roles ?? [];
  if (roles.includes("admin")) return "admin";
  if (roles.includes("expert")) return "expert";
  if (roles.includes("user")) return "user";
  return "anonymous";
}

export default function KeycloakProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { setAuth, clearAuth, setLoading } = useAuthStore();
  const initialized = useRef(false);

  useEffect(() => {
    // Verhindert doppelte Initialisierung in React StrictMode
    if (initialized.current) return;
    initialized.current = true;

    console.log("[DEBUG] keycloak object:", keycloak);
    if (!keycloak) {
      console.log("[DEBUG] keycloak ist undefined, breche ab");
      clearAuth();
      setLoading(false);
      return;
    }
    const kc = keycloak;

    console.log("[DEBUG] rufe kc.init() auf...");
    kc
      .init({
        onLoad: "check-sso",
        silentCheckSsoRedirectUri:
          typeof window !== "undefined"
            ? `${window.location.origin}/silent-check-sso.html`
            : undefined,
        pkceMethod: "S256",
      })
      .then((authenticated) => {
        console.log("[DEBUG] kc.init() erfolgreich, authenticated =", authenticated);
        if (authenticated) {
          const role = extractRole(kc.tokenParsed);
          setAuth({
            isAuthenticated: true,
            token: kc.token ?? null,
            userRole: role,
            username:
              kc.tokenParsed?.preferred_username ?? null,
            email: kc.tokenParsed?.email ?? null,
          });
        } else {
          clearAuth();
        }
      })
      .catch((err) => {
        console.log("[DEBUG] kc.init() fehlgeschlagen:", err);
        clearAuth();
      });

    // Token automatisch alle 60 Sekunden erneuern
    const interval = setInterval(() => {
      if (kc.authenticated) {
        kc
          .updateToken(60)
          .then((refreshed) => {
            if (refreshed) {
              const role = extractRole(kc.tokenParsed);
              setAuth({
                isAuthenticated: true,
                token: kc.token ?? null,
                userRole: role,
                username:
                  kc.tokenParsed?.preferred_username ?? null,
                email: kc.tokenParsed?.email ?? null,
              });
            }
          })
          .catch(() => {
            clearAuth();
          });
      }
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  return <>{children}</>;
}